import { useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage, cn } from '@vibesites/ui-primitives';
import { BedDouble, Bath, Ruler } from 'lucide-react';
import { btnPrimary, btnSecondary, clean, hasValue, linkTarget } from '../utils/content';

interface Unit {
  apply_url?: string | null;
  available?: boolean | null;
}

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_description?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_baths_max?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
  floorplan_is_disabled?: boolean | null;
  units?: Unit[] | null;
}

const applyDef: SlotGroupDefinition = {
  label: 'Card Apply Button',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      defaultValue: '/contact-us',
      suggestedFields: ['property_apply_now_url', 'property_online_leasing_url'],
    },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'apply_now_clicked' },
  },
};

const tourDef: SlotGroupDefinition = {
  label: 'Card Tour Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'schedule_tour_clicked' },
  },
};

const STAGGER = ['', 'js-d1', 'js-d2'];

type FilterKey = 'all' | 'studio' | 'one' | 'two' | 'three' | 'townhome';

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'studio', label: 'Studio' },
  { key: 'one', label: '1 Bed' },
  { key: 'two', label: '2 Bed' },
  { key: 'three', label: '3 Bed' },
  { key: 'townhome', label: 'Townhome' },
];

function num(v: unknown): number | null {
  const n = parseFloat(clean(v).replace(/[^0-9.]/g, ''));
  return isNaN(n) ? null : n;
}

function matches(plan: Floorplan, key: FilterKey): boolean {
  if (key === 'all') return true;
  if (key === 'townhome') return `${clean(plan.floorplan_name)} ${clean(plan.floorplan_description)}`.toLowerCase().includes('town');
  const beds = num(plan.floorplan_beds_min) ?? 0;
  const target = { studio: 0, one: 1, two: 2, three: 3 }[key];
  return key === 'three' ? beds >= 3 : beds === target;
}

function bedLabel(plan: Floorplan): string {
  const beds = num(plan.floorplan_beds_min);
  if (beds == null) return '';
  return beds === 0 ? 'Studio' : `${beds} Bed`;
}

function bathLabel(plan: Floorplan): string {
  const baths = num(plan.floorplan_baths_min);
  return baths == null ? '' : `${baths} Bath`;
}

function sqftLabel(plan: Floorplan): string {
  const min = num(plan.floorplan_sqft_min);
  const max = num(plan.floorplan_sqft_max);
  if (min == null) return '';
  const fmt = (n: number) => n.toLocaleString('en-US');
  return max && max > min ? `${fmt(min)} - ${fmt(max)} Sq. Ft.` : `${fmt(min)} Sq. Ft.`;
}

function priceLabel(plan: Floorplan): string {
  const min = num(plan.floorplan_price_min);
  if (!min) return 'Call for Pricing';
  return `Starting at $${Math.round(min).toLocaleString('en-US')}`;
}

export function FloorplansList() {
  const plans = useCollectionBinding<Floorplan>('floorplanslist.items', { collection: 'floorplans' });
  const apply = useSlotGroup('floorplanslist.apply', applyDef);
  const tour = useSlotGroup('floorplanslist.tour', tourDef);
  const emptyText = useContentToken('floorplanslist.empty_text');
  const disclaimer = useContentToken('floorplanslist.disclaimer');
  const [filter, setFilter] = useState<FilterKey>('all');

  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get('bedroom') as FilterKey | null;
    if (param && FILTERS.some((f) => f.key === param)) setFilter(param);
  }, []);

  const visible = useMemo(() => (plans || []).filter((p) => p.floorplan_is_disabled !== true && hasValue(p.floorplan_name)), [plans]);
  const available = FILTERS.filter((f) => f.key === 'all' || visible.some((p) => matches(p, f.key)));
  const shown = visible.filter((p) => matches(p, filter));

  // Card buttons share one apply/tour setting; the first card carries the editable wrappers.
  const applyButton = (url: string | null | undefined) => (
    <a
      href={url || '/contact-us'}
      target={linkTarget(url, apply._target)}
      rel="noopener noreferrer"
      data-vibesite-event={apply.vibesite_event || undefined}
      className={`${btnPrimary} !min-h-[44px] !px-3 !text-[15px]`}
    >
      {apply.label || 'Apply Now'}
    </a>
  );
  const tourButton = (
    <a
      href={tour.url || '/schedule-a-tour'}
      target={linkTarget(tour.url, tour._target)}
      data-vibesite-event={tour.vibesite_event || undefined}
      className={`${btnSecondary} !min-h-[44px] !px-3 !text-[15px]`}
    >
      {tour.label || 'Schedule a Tour'}
    </a>
  );

  return (
    <section className="bg-background pb-16 pt-12 md:pb-20 md:pt-16">
      <div className="mx-auto max-w-[1240px] px-6 lg:px-10">
        {available.length > 2 && (
          <div role="tablist" aria-label="Filter floor plans" className="mb-10 flex flex-wrap justify-center gap-2">
            {available.map((f) => (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={filter === f.key}
                onClick={() => setFilter(f.key)}
                className={cn(
                  'min-h-[44px] px-5 font-heading text-[16px] font-bold uppercase tracking-wide transition-[background-color,color,transform,box-shadow] duration-300 ease-out hover:-translate-y-0.5 active:scale-95',
                  filter === f.key ? 'bg-primary text-[color:var(--vibe-color-text-on-primary)] shadow-[0_8px_18px_-10px_rgba(0,0,0,0.5)]' : 'bg-muted text-foreground hover:bg-secondary hover:text-[color:var(--vibe-color-text-on-secondary)]',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        <EditableCollectionBinding bindingPath="floorplanslist.items" label="Floor Plans">
          {shown.length === 0 ? (
            <div className="js-reveal py-10 text-center">
              {visible.length === 0 ? (
                <>
                  <EditableToken path="floorplanslist.empty_text" as="p" className="js-copy text-[color:var(--vibe-color-text-muted)]">
                    {emptyText}
                  </EditableToken>
                  <a href="/contact-us" className={`${btnPrimary} mt-6`}>
                    Contact Us
                  </a>
                </>
              ) : (
                <p className="js-copy text-[color:var(--vibe-color-text-muted)]">No floor plans match this filter right now.</p>
              )}
            </div>
          ) : (
            <ul key={filter} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((plan, i) => {
                const availableCount = num(plan.floorplan_available_units);
                const unitApply = (plan.units || []).find((u) => hasValue(u?.apply_url))?.apply_url;
                const applyUrl = hasValue(unitApply) ? unitApply : apply.url;
                const facts = [
                  { icon: BedDouble, text: bedLabel(plan) },
                  { icon: Bath, text: bathLabel(plan) },
                  { icon: Ruler, text: sqftLabel(plan) },
                ].filter((f) => f.text);
                return (
                  <li key={plan.floorplan_id || `${plan.floorplan_name}-${i}`} className={`js-reveal ${STAGGER[i % 3]} js-zoom flex flex-col border border-border bg-background shadow-sm transition-[box-shadow,transform] duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_24px_44px_-22px_rgba(0,0,0,0.45)]`}>
                    <div className="relative aspect-[4/3] overflow-hidden bg-background p-5">
                      {hasValue(plan.floorplan_image_url) ? (
                        <VibesitesImage
                          url={plan.floorplan_image_url}
                          alt={`${clean(plan.floorplan_name)} floor plan`}
                          sizes="(min-width: 1024px) 380px, (min-width: 640px) 50vw, 100vw"
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center bg-muted font-body text-[color:var(--vibe-color-text-muted)]">Floor plan coming soon</div>
                      )}
                      {availableCount != null && availableCount > 0 && (
                        <span className="absolute left-4 top-4 bg-secondary px-3 py-1 font-body text-[13px] font-semibold uppercase tracking-wide text-[color:var(--vibe-color-text-on-secondary)]">
                          {availableCount} Available
                        </span>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col border-t border-border px-6 pb-6 pt-5">
                      <h3 className="js-heading text-[26px] text-foreground">{clean(plan.floorplan_name)}</h3>
                      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2 font-body text-[16px] text-foreground">
                        {facts.map(({ icon: Icon, text }) => (
                          <li key={text} className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-primary" aria-hidden />
                            {text}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-3 font-body text-[17px] font-semibold text-primary">{priceLabel(plan)}</p>
                      <div className="mt-auto grid grid-cols-2 gap-3 pt-6">
                        {i === 0 ? (
                          <EditableSlotGroup groupPath="floorplanslist.apply" definition={applyDef} className="grid">
                            {applyButton(applyUrl)}
                          </EditableSlotGroup>
                        ) : (
                          applyButton(applyUrl)
                        )}
                        {i === 0 ? (
                          <EditableSlotGroup groupPath="floorplanslist.tour" definition={tourDef} className="grid">
                            {tourButton}
                          </EditableSlotGroup>
                        ) : (
                          tourButton
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </EditableCollectionBinding>

        <EditableToken path="floorplanslist.disclaimer" as="p" className="mx-auto mt-10 max-w-[980px] text-center font-body text-[13px] leading-relaxed text-[color:var(--vibe-color-text-muted)]">
          {disclaimer}
        </EditableToken>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.empty_text': {
      label: 'No Results Message',
      type: 'text',
      defaultValue: 'Contact our leasing team for current floor plans, pricing, and availability.',
    },
    'floorplanslist.disclaimer': {
      label: 'Disclaimer',
      type: 'text',
      defaultValue:
        'Floor plans are an artist’s interpretation. All dimensions are approximate. Actual product and specifications may vary in dimension or detail. Not all features are available in every apartment. Prices and availability are subject to change.',
    },
  },
  slotGroups: {
    'floorplanslist.apply': applyDef,
    'floorplanslist.tour': tourDef,
  },
  collectionBindings: {
    'floorplanslist.items': { label: 'Floor Plans', collection: 'floorplans' },
  },
};

(FloorplansList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorplansList;
